import "./Dashboard.scss";

import {
  Box,
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  isWidthUp,
  Typography,
  withWidth,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { InvestmentStatusValues } from "common/backend/types/utils";
import { CommonErrorDialog } from "common/components/CommonErrorDialog";
import { Checkbox, SubmitButton } from "common/components/input/elements";
import { GetCurrentUserInvestments, UpdateInvestment } from "common/graphql/queryDescriptors";
import { useCustomQuery } from "common/store/hooks/useCustomQuery";
import { InvestmentManageable, UpdateInvestmentManageable } from "common/store/investment/types";
import { StoreState } from "common/store/types";
import { toUpperCaseFirst } from "common/utils/utility";
import { errorMessages } from "common/utils/validation";
import { ContentWrapper } from "components/ContentWrapper";
import Navbar from "components/Navbar";
import { Form, Formik } from "formik";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { DocumentType, ShowFile, showFile } from "services/fake/fakeDocumentManager";

const RevocationModal = ({
  open, setOpen, investmentID
}: any) => {
  // State
  const [error, setError] = useState("");

  // Hooks
  const { t } = useTranslation();
  const {
    execute: updateInvestment, loading: loadingUpdateInvestment
  } = useCustomQuery(UpdateInvestment({
    onError: () => {
      setError(t("Error"));
    },
    onSuccess: () => {
      setOpen(false);
    },
  }));

  // Options
  const initialValues = new UpdateInvestmentManageable({
    id: investmentID,
    status: InvestmentStatusValues.REVOKE_REQUESTED,
  });

  // Validation
  const onValidate = (values: UpdateInvestmentManageable) => {
    return new UpdateInvestmentManageable(values).validate(
      Object.keys(values),
      errorMessages
    );
  };

  const onSubmit = async (
    values: UpdateInvestmentManageable, { setSubmitting }: any
  ) => {
    setSubmitting(true);
    await updateInvestment({ variables: { input: new UpdateInvestmentManageable(values).serialize() } });
    setSubmitting(false);
  };

  return (
    <React.Fragment>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        aria-labelledby="customized-dialog-title"
      >
        <DialogTitle>{t("Revoke Investment")}</DialogTitle>

        <IconButton
          className="close-icon--dialog"
          aria-label="close"
          onClick={() => setOpen(false)}
        >
          <CloseIcon />
        </IconButton>

        <DialogContent>
          <Formik
            initialValues={initialValues}
            validate={onValidate}
            onSubmit={onSubmit}
          >
            {(props) => {
              const {
                errors,
                isSubmitting,
                setFieldValue,
                touched,
                values,
              } = props;

              return (
                <Form>
                  <Grid container spacing={1}>
                    <Grid container item>
                      <Grid item xs={12} md={8}>
                        <Checkbox
                          id="termsRevocation"
                          checked={values.termsRevocation}
                          error={touched && touched.termsRevocation && errors && errors.termsRevocation}
                          onChange={(e: any) => setFieldValue(
                            "termsRevocation",
                            e.target.checked
                          )}
                          label={t("I agree to the revocation T&C")}
                        />
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <Button
                          className="primary--medium"
                          onClick={() =>
                            showFile(
                              ShowFile.Open,
                              DocumentType.Revocation
                            )
                          }
                          style={{}}
                          fullWidth
                          variant="contained"
                        >
                          {t("Read")}
                        </Button>
                      </Grid>
                    </Grid>

                    <Grid item xs={12}>
                      <SubmitButton
                        value={t("Revoke")}
                        {...props}
                        isSubmitting={loadingUpdateInvestment || isSubmitting}
                      />
                    </Grid>
                  </Grid>
                </Form>
              );
            }}
          </Formik>
        </DialogContent>
      </Dialog>

      <CommonErrorDialog error={error} setError={setError} />
    </React.Fragment>
  );
};

const DashboardCard = ({
  title,
  subtitle,
  status,
  amount,
  showExchangeButton,
  showRevokeButton,
  isFixed,
  grayed,
  investmentID,
}: any) => {
  // State
  const [revokeModalOpen, setRevokeModalOpen] = useState(false);

  // Hooks
  const { t } = useTranslation();

  // Helpers
  const statusReadable = (sentence: string) : string => {
    return sentence.split('_').map((word: string) => toUpperCaseFirst(word.toLowerCase())).join(' ');
  }

  return (
    <Card
      className={"card" + (grayed ? "--grayed" : "")}
      style={isFixed ? { width: "362px" } : {}}
    >
      {title &&
        <Typography className={"title" + (grayed ? " line-through" : "")}>
          {title}
        </Typography>
      }

      <Typography className={"subtitle" + (grayed ? " line-through" : "")}>
        {subtitle}
      </Typography>

      {status &&
        <Typography className={"subtitle" + (grayed ? " line-through" : "")}>
          {statusReadable(status)}
        </Typography>
      }

      <Grid container direction={isFixed ? "row" : "column"}>
        <Grid item xs={7}>
          <Typography
            variant="subtitle2"
            className={"amount" + (grayed ? " line-through" : "")}
          >
            € {amount}
          </Typography>
        </Grid>

        <Grid item xs={5} container direction="column" spacing={3}>
          {showExchangeButton &&
            <Grid item className="grid-container">
              <Button className="secondary--small">{t("Exchange")}</Button>
            </Grid>
          }

          {showRevokeButton &&
            <Grid item className="grid-container">
              <Button
                className="secondary--small"
                onClick={() => setRevokeModalOpen(true)}
              >
                {t("Revoke")}
              </Button>
            </Grid>
          }
        </Grid>
      </Grid>

      <RevocationModal
        open={revokeModalOpen}
        setOpen={setRevokeModalOpen}
        investmentID={investmentID}
      />
    </Card>
  );
};

function Dashboard({ width }: any) {
  // Hooks
  const { t } = useTranslation();
  const investments = useSelector<StoreState, Array<InvestmentManageable>>((state) => state.investments);

  // Hooks
  useCustomQuery(GetCurrentUserInvestments({}));

  // Helpers
  const getTotalAssets = () => {
    let total = 0;
    investments.map((investment: InvestmentManageable) => total += investment.amountShares);
    return total;
  };

  return (
    <Box>
      <Navbar />

      <ContentWrapper>
        <Box className="dashboard">
          <Grid item className="section">
            <Typography className="section-title">
              {t("Total assets")}
            </Typography>

            <DashboardCard
              subtitle={t("Total value")}
              amount={
                investments && investments.length > 0 ? getTotalAssets() : 0
              }
              isFixed={isWidthUp(
                "sm",
                width
              )}
            />
          </Grid>

          {investments && investments.length > 0 &&
            <Grid item className="section">
              <Typography className="section-title">
                {t("My Investments")}
              </Typography>

              <Grid container spacing={isWidthUp(
                "lg",
                width
              ) ? 9 : 3}>
                {investments.map((
                  investment: InvestmentManageable, i: number
                ) =>
                  <Grid item key={i}>
                    <DashboardCard
                      title={t("Sale #") + investment.saleId}
                      investmentID={investment.id}
                      amount={investment.amountShares}
                      status={investment.status}
                      grayed={
                        investment.status === InvestmentStatusValues.REVOKED
                      }
                      showExchangeButton={
                        investment.status !== InvestmentStatusValues.REVOKED
                      }
                      showRevokeButton={investment.isRevocable()}
                      isFixed={isWidthUp(
                        "sm",
                        width
                      )}
                    />
                  </Grid>)}
              </Grid>
            </Grid>
          }
        </Box>
      </ContentWrapper>
    </Box>
  );
}

export default withWidth()(Dashboard);
